<template>
  <section
    id="plan-comments"
    :style="{ flex: '1 1 auto', display: 'flex', flexDirection: 'column' }">
    <a-divider>Комментарии</a-divider>

    <rich-text-editor
      v-model="data.comment"
      :upload-image="uploadCommentImage"
      height="250px" />

    <div :style="{ marginTop: '.75rem' }">
      <a-button
        type="primary"
        :disabled="!data.comment.length"
        @click="submitComment">
        Подтвердить
      </a-button>
    </div>

    <a-list
      class="comment-list"
      :header="`${paginator.total} комментариев`"
      item-layout="horizontal"
      :loading="loading"
      :data-source="paginator.items">
      <template #renderItem="{ item }">
        <a-list-item :style="{ padding: 0 }">
          <a-comment
            class="comment-wrapper"
            :style="{ width: '100%' }">
            <template #author>
              <a>{{ item.user.name }}</a>
            </template>

            <template #content>
              <div class="markdown-content">
                <rich-text-content
                  class="comment-content"
                  :value="item.text" />
              </div>
            </template>

            <template #datetime>
              <a-tooltip :title="moment(item.created_at).format('DD.MM.YYYY HH:mm:ss')">
                <span>{{ moment().from(item.created_at) }}</span>
              </a-tooltip>
            </template>

            <template #actions>
              <span
                v-if="item.user_id === auth.id"
                @click="deleteComment(item.id)">
                Удалить
              </span>
            </template>
          </a-comment>
        </a-list-item>
      </template>
    </a-list>

    <div :style="{ display: 'flex', justifyContent: 'center', padding: '1.25rem 0' }">
      <a-button
        v-if="isMoreButtonShow"
        type="primary"
        @click="loadComments()">
        Загрузить ещё
      </a-button>
    </div>
  </section>
</template>

<script>
import {
  computed, defineComponent, onMounted, onUnmounted, reactive, ref, defineExpose, watch,
} from 'vue';
import moment from 'moment';
import { httpClient } from '@/httpClient';
import { api } from '@/api';
import { Modal, message } from 'ant-design-vue';
import RichTextEditor from '@/Components/Common/RichTextEditor.vue';
import RichTextContent from '@/Components/Common/RichTextContent.vue';

export default defineComponent({
  name: 'PlanComments',

  components: {
    RichTextEditor,
    RichTextContent,
  },

  props: {
    id: { default: () => null, type: Number },
    auth: { default: () => ({}), type: Object },
    type: { default: () => null, type: Number },
    isVerificationMode: { type: Boolean, default: false },
    expectedVerificationComments: { type: Number, default: 0 },
    updateActivityLogs: { type: Function, default: null },
  },

  emits: ['published-comment', 'verification-completed'],

  setup(props, { emit }) {
    const paginator = reactive({
      items: [],
      currentPage: 1,
      total: 0,
      showSizeChanger: false,
    });

    const loading = ref(false);
    const data = reactive({ comment: '' });
    let refreshIntervalId = null;

    const commentsCount = computed(() => paginator.items.length);
    const totalComments = computed(() => paginator.total);

    const verificationBaseCount = ref(0);
    const expectedVerificationComments = ref(props.expectedVerificationComments);
    const verificationExpectedCount = ref(0);
    const isVerificationComplete = ref(false);

    const uploadCommentImage = async (file) => {
      const { data: uploaded } = await api.uploadCommentImage({ planId: props.id, file });
      if (!uploaded.success) throw new Error(uploaded.message || 'Не удалось загрузить изображение');

      return uploaded.url;
    };

    const loadComments = (page = null) => {
      loading.value = true;
      const url = `/api/crm/plans/${props.id}/comments`;
      const params = { page: paginator.currentPage + 1, type: props.type };

      if (page) {
        paginator.currentPage = page;
        params.page = page;
      }

      return httpClient.get(url, { params })
        .then((resp) => {
          const { items, total, currentPage } = resp.data;
          if (page) paginator.items = [];
          paginator.items = paginator.items.concat(items);
          paginator.total = total;
          paginator.currentPage = currentPage;
        })
        .finally(() => (loading.value = false));
    };

    // История изменений меняется только когда комментарий добавлен/удалён,
    // поэтому дёргаем её точечно, а не на каждой загрузке списка.
    const refreshActivityLogs = () => {
      if (props.updateActivityLogs) {
        props.updateActivityLogs();
      }
    };

    const submitComment = () => {
      const url = `/api/crm/plans/${props.id}/comments`;
      const params = { text: data.comment, type: props.type };

      httpClient.post(url, params).then(() => {
        data.comment = '';
        loadComments(1).then(refreshActivityLogs);
        message.success('Комментарий добавлен');
        emit('published-comment');
      }).catch((error) => {
        console.error('Error submitting comment:', error);
        message.error('Ошибка при отправке комментария');
      });
    };

    const deleteComment = (commentId) => {
      const url = `/api/crm/plans/${props.id}/comments/${commentId}`;
      Modal.confirm({
        title: 'Вы уверены, что хотите удалить комментарий?',
        cancelText: 'Отмена',
        okText: 'Удалить',
        okType: 'danger',
        onOk: () => httpClient.delete(url).then(() => {
          data.comment = '';
          loadComments(1).then(refreshActivityLogs);
          message.success('Комментарий удален');
        }).catch((error) => {
          console.error('Error deleting comment:', error);
          message.error('Ошибка при удалении комментария');
        }),
      });
    };

    const stopVerificationMonitoring = () => {
      if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
        refreshIntervalId = null;
      }
      verificationBaseCount.value = 0;
      verificationExpectedCount.value = 0;
      isVerificationComplete.value = false;
    };

    const startVerificationMonitoring = () => {
      if (refreshIntervalId) clearInterval(refreshIntervalId);

      verificationBaseCount.value = paginator.items.length;
      isVerificationComplete.value = false;

      message.info(`Ожидается ${verificationExpectedCount.value} комментариев...`);

      refreshIntervalId = setInterval(() => {
        loadComments(1)
          .then(() => {
            const currentCount = paginator.items.length;
            const newCommentsCount = currentCount - verificationBaseCount.value;

            if (newCommentsCount >= verificationExpectedCount.value) {
              stopVerificationMonitoring();
              message.success(`Проверка завершена! Получено ${newCommentsCount} новых комментариев`);
              emit('verification-completed');
            } else {
              message.info(`Проверка в процессе... (получено ${newCommentsCount}/${verificationExpectedCount.value})`);
            }
          })
          .catch((error) => console.error('Ошибка при обновлении комментариев:', error));
      }, 10000);
    };

    watch(() => props.isVerificationMode, (newValue) => {
      if (newValue) {
        verificationExpectedCount.value = props.expectedVerificationComments;
        startVerificationMonitoring();
      } else {
        stopVerificationMonitoring();
      }
    });

    watch(() => props.expectedVerificationComments, (newValue) => {
      if (props.isVerificationMode && newValue > 0) {
        verificationExpectedCount.value = newValue;
      }
    });

    const isMoreButtonShow = computed(() => paginator.total > paginator.items.length);

    onMounted(() => {
      loadComments(1);
    });

    onUnmounted(() => {
      stopVerificationMonitoring();
    });

    defineExpose({
      loadComments,
      commentsCount,
      totalComments,
      getComments: () => paginator.items,
      startVerificationMonitoring,
      stopVerificationMonitoring,
      verificationBaseCount,
      verificationExpectedCount,
    });

    return {
      data,
      moment,
      loading,
      paginator,
      isMoreButtonShow,
      submitComment,
      loadComments,
      deleteComment,
      commentsCount,
      totalComments,
      uploadCommentImage,
      isVerificationComplete,
    };
  },
});
</script>

<style scoped>
.comment-content {
  white-space: unset;
  margin-top: 10px;
  margin-bottom: 0 !important;
  padding: 0 !important;
}

.comment-list {
  flex: 1;
  overflow-y: auto;
}

.comment-wrapper {
  width: 100%;
}
</style>

<style>
.markdown-content {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.85);
  width: 100%;
}

.markdown-content table {
  border-collapse: collapse;
  width: 100%;
  margin: 16px 0;
  font-size: 14px;
  border: 1px solid #d9d9d9;
}

.markdown-content th,
.markdown-content td {
  border: 1px solid #d9d9d9;
  padding: 8px 12px;
  text-align: left;
}

.markdown-content th {
  background-color: #fafafa;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}

.markdown-content tr:nth-child(even) {
  background-color: #f8f9fa;
}

.markdown-content tr:hover {
  background-color: #f0f0f0;
}

.markdown-content h1,
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  margin-top: 1em;
  margin-bottom: 0.5em;
  font-weight: 600;
  line-height: 1.25;
}

.markdown-content p {
  margin-bottom: 1em;
}

.markdown-content ul,
.markdown-content ol {
  padding-left: 2em;
  margin-bottom: 1em;
}

.markdown-content li {
  margin-bottom: 0.25em;
}

.markdown-content code {
  background-color: rgba(175, 184, 193, 0.2);
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-size: 0.85em;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
}

.markdown-content pre {
  background-color: #f6f8fa;
  padding: 16px;
  border-radius: 6px;
  overflow: auto;
  margin: 16px 0;
}

.markdown-content pre code {
  background: none;
  padding: 0;
}

.markdown-content blockquote {
  border-left: 4px solid #d0d7de;
  padding-left: 16px;
  margin: 16px 0;
  color: #656d76;
}

.markdown-content img {
  max-width: 100%;
  height: auto;
}

.markdown-content a {
  color: #0969da;
  text-decoration: none;
}

.markdown-content a:hover {
  text-decoration: underline;
}

.markdown-content .hljs-wrapper {
  margin: 16px 0;
  border-radius: 6px;
}

.markdown-content .hljs {
  background: transparent;
  padding: 0;
  font-size: 13px;
  line-height: 1.45;
}

@media (max-width: 768px) {
  .markdown-content table {
    font-size: 12px;
  }

  .markdown-content th,
  .markdown-content td {
    padding: 6px 8px;
  }

  .markdown-content pre {
    padding: 12px;
  }
}
</style>
